'use client';

import { useEffect, useState } from 'react';

/**
 * The client's clock for a render that must hydrate cleanly: `null` on the
 * server AND on the first client render (both paint the same clock-free
 * markup - the rule `RelativeTime` / `DateOnly` follow with their ISO first
 * paint), then the real `Date.now()` after mount, refreshed on an interval so
 * a "Due today" turns into "1 d late" at midnight without a reload.
 *
 * Call it ONCE per surface (a table, a feed) and pass the value down: one
 * timer per surface, not one per row.
 *
 * `intervalMs: null` reads the clock once, at mount, and never ticks: for a
 * face that needs only to know it runs on the client (the viewer's locale),
 * not the passing time (`DateOnly`).
 */
export function useNow(intervalMs: number | null = 60_000): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    if (intervalMs === null) return;
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
